var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};

// src/index.tsx
import { ThemeProvider } from "next-themes";
import { useRouter as useRouter7 } from "nextra/hooks";
import { MDXProvider } from "nextra/mdx";

// src/components/anchor.tsx
import cn from "clsx";
import NextLink from "next/link";
import { forwardRef } from "react";
import { jsx } from "react/jsx-runtime";
var Anchor = forwardRef(
  (_a, forwardedRef) => {
    var _b = _a, { href = "", children, newWindow } = _b, props = __objRest(_b, ["href", "children", "newWindow"]);
    const ComponentToUse = newWindow || href.startsWith("#") ? "a" : NextLink;
    return /* @__PURE__ */ jsx(
      ComponentToUse,
      __spreadProps(__spreadValues(__spreadProps(__spreadValues({}, props), {
        className: cn("nextra-focus", props.className),
        ref: forwardedRef,
        href
      }), newWindow && {
        target: "_blank",
        rel: "noreferrer"
      }), {
        children
      })
    );
  }
);
Anchor.displayName = "Anchor";

// src/components/banner.tsx
import cn2 from "clsx";
import { Button } from "nextra/components";
import { XIcon } from "nextra/icons";

// src/contexts/active-anchor.tsx
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { jsx as jsx2 } from "react/jsx-runtime";
var ActiveAnchorContext = createContext(null);
ActiveAnchorContext.displayName = "ActiveAnchor";
var SetActiveAnchorContext = createContext(null);
SetActiveAnchorContext.displayName = "SetActiveAnchor";
var IntersectionObserverContext = createContext(
  null
);
IntersectionObserverContext.displayName = "IntersectionObserver";
var slugs = /* @__PURE__ */ new WeakMap();
var SlugsContext = createContext(slugs);
SlugsContext.displayName = "Slugs";
var useActiveAnchor = () => useContext(ActiveAnchorContext);
var useSetActiveAnchor = () => useContext(SetActiveAnchorContext);
var useIntersectionObserver = () => useContext(IntersectionObserverContext);
var useSlugs = () => useContext(SlugsContext);
var ActiveAnchorProvider = ({
  children
}) => {
  const [activeAnchor, setActiveAnchor] = useState({});
  const observerRef = useRef(null);
  useEffect(() => {
    const navbarHeight = getComputedStyle(document.body).getPropertyValue(
      "--nextra-navbar-height"
    );
    observerRef.current = new IntersectionObserver(
      (entries) => {
        setActiveAnchor((f) => {
          const ret = __spreadValues({}, f);
          for (const entry of entries) {
            if ((entry == null ? void 0 : entry.rootBounds) && slugs.has(entry.target)) {
              const [slug, index] = slugs.get(entry.target);
              const aboveHalfViewport = entry.boundingClientRect.y + entry.boundingClientRect.height <= entry.rootBounds.y + entry.rootBounds.height;
              const insideHalfViewport = entry.intersectionRatio > 0;
              ret[slug] = {
                index,
                aboveHalfViewport,
                insideHalfViewport
              };
            }
          }
          let activeSlug = "";
          let smallestIndexInViewport = Infinity;
          let largestIndexAboveViewport = -1;
          for (const s in ret) {
            ret[s].isActive = false;
            if (ret[s].insideHalfViewport && ret[s].index < smallestIndexInViewport) {
              smallestIndexInViewport = ret[s].index;
              activeSlug = s;
            }
            if (smallestIndexInViewport === Infinity && ret[s].aboveHalfViewport && ret[s].index > largestIndexAboveViewport) {
              largestIndexAboveViewport = ret[s].index;
              activeSlug = s;
            }
          }
          if (ret[activeSlug]) ret[activeSlug].isActive = true;
          return ret;
        });
      },
      {
        rootMargin: `-${navbarHeight} 0px -50%`,
        threshold: [0, 1]
      }
    );
    return () => {
      observerRef.current.disconnect();
    };
  }, []);
  return /* @__PURE__ */ jsx2(ActiveAnchorContext.Provider, { value: activeAnchor, children: /* @__PURE__ */ jsx2(SetActiveAnchorContext.Provider, { value: setActiveAnchor, children: /* @__PURE__ */ jsx2(SlugsContext.Provider, { value: slugs, children: /* @__PURE__ */ jsx2(IntersectionObserverContext.Provider, { value: observerRef.current, children }) }) }) });
};

// src/contexts/config.tsx
import { useRouter } from "next/router";
import { useFSRoute } from "nextra/hooks";
import { normalizePages } from "nextra/normalize-pages";
import { createContext as createContext3, useContext as useContext3, useEffect as useEffect2, useMemo, useState as useState2 } from "react";

// src/contexts/menu.ts
import { createContext as createContext2, useContext as useContext2 } from "react";
var MenuContext = createContext2({
  menu: false,
  setMenu: () => false
});
MenuContext.displayName = "Menu";
var useMenu = () => useContext2(MenuContext);
var MenuProvider = MenuContext.Provider;

// src/contexts/config.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
var ConfigContext = createContext3({
  title: "",
  frontMatter: {},
  filePath: "",
  hideSidebar: false,
  normalizePagesResult: {}
});
ConfigContext.displayName = "Config";
function useConfig() {
  return useContext3(ConfigContext);
}
function ConfigProvider({
  children,
  value: pageOpts
}) {
  const [menu, setMenu] = useState2(false);
  const { asPath } = useRouter();
  const fsPath = useFSRoute();
  const normalizePagesResult = useMemo(
    () => normalizePages({ list: pageOpts.pageMap, route: fsPath }),
    [pageOpts.pageMap, fsPath]
  );
  const { activeType, activeThemeContext: themeContext } = normalizePagesResult;
  const extendedConfig = {
    title: pageOpts.title,
    frontMatter: pageOpts.frontMatter,
    filePath: pageOpts.filePath,
    timestamp: pageOpts.timestamp,
    hideSidebar: !themeContext.sidebar || themeContext.layout === "raw" || activeType === "page",
    normalizePagesResult
  };
  useEffect2(() => {
    setMenu(false);
  }, [asPath]);
  useEffect2(() => {
    document.body.classList.toggle("max-md:_overflow-hidden", menu);
  }, [menu]);
  useEffect2(() => {
    let resizeTimer;
    function addResizingClass() {
      document.body.classList.add("resizing");
      clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        document.body.classList.remove("resizing");
      }, 200);
    }
    window.addEventListener("resize", addResizingClass);
    return () => {
      window.removeEventListener("resize", addResizingClass);
    };
  }, []);
  const value = useMemo(() => ({ menu, setMenu }), [menu]);
  return /* @__PURE__ */ jsx3(ConfigContext.Provider, { value: extendedConfig, children: /* @__PURE__ */ jsx3(MenuProvider, { value, children }) });
}

// src/contexts/theme-config.tsx
import { createContext as createContext4, useContext as useContext4, useRef as useRef2 } from "react";

// src/constants.tsx
import { useRouter as useRouter2 } from "nextra/hooks";
import { DiscordIcon, GitHubIcon } from "nextra/icons";
import { isValidElement } from "react";

// src/utils/git-url-parse.ts
function gitUrlParse(url) {
  const { href, origin, pathname } = new URL(url);
  const [, owner, name] = pathname.split("/");
  return {
    href,
    origin,
    owner,
    name
  };
}

// src/utils/get-git-issue-url.ts
function getGitIssueUrl({
  repository = "",
  title,
  labels
}) {
  const repo = gitUrlParse(repository);
  if (!repo) throw new Error("Invalid `docsRepositoryBase` URL!");
  if (repo.origin.includes("gitlab")) {
    return `${repo.origin}/${repo.owner}/${repo.name}/-/issues/new?issue[title]=${encodeURIComponent(title)}${labels ? `&issue[description]=/label${encodeURIComponent(` ~${labels}
`)}` : ""}`;
  }
  if (repo.origin.includes("github")) {
    return `${repo.origin}/${repo.owner}/${repo.name}/issues/new?title=${encodeURIComponent(title)}&labels=${labels || ""}`;
  }
  return "#";
}

// src/utils/render.tsx
import { jsx as jsx4 } from "react/jsx-runtime";
function renderComponent(ComponentOrNode, props) {
  if (!ComponentOrNode) return null;
  if (typeof ComponentOrNode !== "function") return ComponentOrNode;
  return /* @__PURE__ */ jsx4(ComponentOrNode, __spreadValues({}, props));
}
function renderString(stringOrFunction, props = {}) {
  const result = typeof stringOrFunction === "function" ? stringOrFunction(props) : stringOrFunction;
  return result || "";
}

// src/utils/use-git-edit-url.ts
function useGitEditUrl(filePath = "") {
  const config = useThemeConfig();
  const repo = gitUrlParse(config.docsRepositoryBase || "");
  if (!repo) throw new Error("Invalid `docsRepositoryBase` URL!");
  return `${repo.href}/${filePath}`;
}

// src/constants.tsx
import { Fragment, jsx as jsx5, jsxs } from "react/jsx-runtime";
var DEFAULT_LOCALE = "en-US";
var DEFAULT_THEME = {
  backgroundColor: {
    dark: "17,17,17",
    light: "250,250,250"
  },
  banner: {
    dismissible: true,
    key: "nextra-banner"
  },
  chat: {
    icon: /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx5(DiscordIcon, {}),
      /* @__PURE__ */ jsx5("span", { className: "_sr-only", children: "Discord" })
    ] })
  },
  color: {
    hue: {
      dark: 204,
      light: 212
    },
    lightness: {
      dark: 55,
      light: 45
    },
    saturation: 100
  },
  darkMode: true,
  direction: "ltr",
  docsRepositoryBase: "https://github.com/shuding/nextra",
  editLink: {
    component: function EditLink({ className, filePath, children }) {
      const editUrl = useGitEditUrl(filePath);
      if (!editUrl) {
        return null;
      }
      return /* @__PURE__ */ jsx5(Anchor, { className, href: editUrl, children });
    },
    content: "Edit this page"
  },
  feedback: {
    content: "Question? Give us feedback \u2192",
    labels: "feedback",
    useLink() {
      const config = useConfig();
      const themeConfig = useThemeConfig();
      return getGitIssueUrl({
        labels: themeConfig.feedback.labels,
        repository: themeConfig.docsRepositoryBase,
        title: `Feedback for \u201C${config.title}\u201D`
      });
    }
  },
  footer: {
    component: Footer,
    content: `MIT ${(/* @__PURE__ */ new Date()).getFullYear()} \xA9 Nextra.`
  },
  gitTimestamp: function GitTimestamp({ timestamp }) {
    const { locale = DEFAULT_LOCALE } = useRouter2();
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      "Last updated on",
      " ",
      /* @__PURE__ */ jsx5("time", { dateTime: timestamp.toISOString(), children: timestamp.toLocaleDateString(locale, {
        day: "numeric",
        month: "long",
        year: "numeric"
      }) })
    ] });
  },
  head: function useHead() {
    const { frontMatter, title: pageTitle } = useConfig();
    const title = `${pageTitle} \u2013 Nextra`;
    const { description, canonical, image } = frontMatter;
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx5("title", { children: title }),
      /* @__PURE__ */ jsx5("meta", { property: "og:title", content: title }),
      description && [
        /* @__PURE__ */ jsx5("meta", { name: "description", content: description }, 0),
        /* @__PURE__ */ jsx5("meta", { property: "og:description", content: description }, 1)
      ],
      canonical && /* @__PURE__ */ jsx5("link", { rel: "canonical", href: canonical }),
      image && /* @__PURE__ */ jsx5("meta", { name: "og:image", content: image })
    ] });
  },
  i18n: [],
  logo: /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx5("span", { className: "_font-extrabold", children: "Nextra" }),
    /* @__PURE__ */ jsx5("span", { className: "_ml-2 max-md:_hidden _font-normal _text-gray-600", children: "The Next Docs Builder" })
  ] }),
  logoLink: true,
  navbar: {
    component: Navbar
  },
  navigation: true,
  nextThemes: {
    defaultTheme: "system",
    storageKey: "theme"
  },
  notFound: {
    content: "Submit an issue about broken link \u2192",
    labels: "bug"
  },
  project: {
    icon: /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx5(GitHubIcon, {}),
      /* @__PURE__ */ jsx5("span", { className: "_sr-only", children: "GitHub" })
    ] })
  },
  search: {
    component: Flexsearch,
    emptyResult: /* @__PURE__ */ jsx5("span", { className: "_block _select-none _p-8 _text-center _text-sm _text-gray-400", children: "No results found." }),
    error: "Failed to load search index.",
    loading: "Loading\u2026",
    placeholder: "Search documentation\u2026"
  },
  sidebar: {
    defaultMenuCollapseLevel: 2,
    toggleButton: true
  },
  themeSwitch: {
    component: ThemeSwitch,
    useOptions: { dark: "Dark", light: "Light", system: "System" }
  },
  toc: {
    backToTop: "Scroll to top",
    component: TOC,
    float: true,
    title: "On This Page"
  }
};
var DEEP_OBJECT_KEYS = Object.entries(DEFAULT_THEME).map(([key, value]) => {
  const isObject = value && typeof value === "object" && !Array.isArray(value) && !isValidElement(value);
  if (isObject) {
    return key;
  }
}).filter(Boolean);

// src/contexts/theme-config.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
var ThemeConfigContext = createContext4(DEFAULT_THEME);
ThemeConfigContext.displayName = "ThemeConfig";
var useThemeConfig = () => useContext4(ThemeConfigContext);
function ThemeConfigProvider({
  value,
  children
}) {
  const storeRef = useRef2();
  storeRef.current || (storeRef.current = __spreadValues(__spreadValues({}, DEFAULT_THEME), value && Object.fromEntries(
    Object.entries(value).map(([key, value2]) => [
      key,
      value2 && typeof value2 === "object" && DEEP_OBJECT_KEYS.includes(key) ? (
        // @ts-expect-error -- key has always object value
        __spreadValues(__spreadValues({}, DEFAULT_THEME[key]), value2)
      ) : value2
    ])
  )));
  return /* @__PURE__ */ jsx6(ThemeConfigContext.Provider, { value: storeRef.current, children });
}

// src/components/banner.tsx
import { Fragment as Fragment2, jsx as jsx7, jsxs as jsxs2 } from "react/jsx-runtime";
function Banner() {
  const { banner } = useThemeConfig();
  if (!banner.content) {
    return null;
  }
  const hideBannerScript = `try{if(localStorage.getItem(${JSON.stringify(
    banner.key
  )})==='0'){document.body.classList.add('nextra-banner-hidden')}}catch(e){}`;
  return /* @__PURE__ */ jsxs2(Fragment2, { children: [
    /* @__PURE__ */ jsx7("script", { dangerouslySetInnerHTML: { __html: hideBannerScript } }),
    /* @__PURE__ */ jsxs2(
      "div",
      {
        className: cn2(
          "nextra-banner-container max-md:_sticky _top-0 _z-20 _flex _items-center",
          "_h-[var(--nextra-banner-height)] [body.nextra-banner-hidden_&]:_hidden",
          "_text-slate-50 dark:_text-white _bg-neutral-900 dark:_bg-[linear-gradient(1deg,#383838,#212121)]",
          "_px-2 _ps-10 print:_hidden"
        ),
        children: [
          /* @__PURE__ */ jsx7("div", { className: "_w-full _truncate _text-center _font-medium _text-sm", children: renderComponent(banner.content) }),
          banner.dismissible && /* @__PURE__ */ jsx7(
            Button,
            {
              "aria-label": "Dismiss banner",
              className: ({ hover }) => cn2("_p-2", hover ? "_opacity-100" : "_opacity-80"),
              onClick: () => {
                try {
                  localStorage.setItem(banner.key, "0");
                } catch (e) {
                }
                document.body.classList.add("nextra-banner-hidden");
              },
              children: /* @__PURE__ */ jsx7(XIcon, { height: "16" })
            }
          )
        ]
      }
    )
  ] });
}

// src/components/bleed.tsx
import cn3 from "clsx";
import { jsx as jsx8 } from "react/jsx-runtime";
function Bleed({
  full,
  children
}) {
  return /* @__PURE__ */ jsx8(
    "div",
    {
      className: cn3(
        "nextra-bleed _relative _-mx-6 _mt-6 md:_-mx-8 2xl:_-mx-24",
        "_z-[1]",
        // for firefox https://github.com/shuding/nextra/issues/2824
        full && [
          // 'md:mx:[calc(-50vw+50%+8rem)',
          "ltr:xl:_ml-[calc(50%-50vw+16rem)] ltr:xl:_mr-[calc(50%-50vw)]",
          "rtl:xl:_ml-[calc(50%-50vw)] rtl:xl:_mr-[calc(50%-50vw+16rem)]"
        ]
      ),
      children
    }
  );
}

// src/components/breadcrumb.tsx
import cn4 from "clsx";
import NextLink2 from "next/link";
import { ArrowRightIcon } from "nextra/icons";
import { Fragment as Fragment3 } from "react";
import { jsx as jsx9, jsxs as jsxs3 } from "react/jsx-runtime";
function Breadcrumb({
  activePath
}) {
  return /* @__PURE__ */ jsx9("div", { className: "nextra-breadcrumb _mt-1.5 _flex _items-center _gap-1 _overflow-hidden _text-sm _text-gray-500 dark:_text-gray-400 contrast-more:_text-current", children: activePath.map((item, index, arr) => {
    const nextItem = arr[index + 1];
    const href = nextItem ? item.withIndexPage ? item.route : item.children[0].route === nextItem.route ? "" : item.children[0].route : "";
    const ComponentToUse = href ? NextLink2 : "span";
    return /* @__PURE__ */ jsxs3(Fragment3, { children: [
      index > 0 && /* @__PURE__ */ jsx9(
        ArrowRightIcon,
        {
          height: "14",
          className: "_shrink-0 rtl:_rotate-180"
        }
      ),
      /* @__PURE__ */ jsx9(
        ComponentToUse,
        __spreadProps(__spreadValues({
          className: cn4(
            "_whitespace-nowrap _transition-colors",
            nextItem ? "_min-w-6 _overflow-hidden _text-ellipsis" : "_font-medium _text-gray-700 contrast-more:_font-bold contrast-more:_text-current dark:_text-gray-100 contrast-more:dark:_text-current",
            href && "nextra-focus _ring-inset hover:_text-gray-900 dark:hover:_text-gray-100"
          ),
          title: item.title
        }, href && { href }), {
          children: item.title
        })
      )
    ] }, item.route + item.name);
  }) });
}

// src/components/collapse.tsx
import cn5 from "clsx";
import { useEffect as useEffect3, useRef as useRef3 } from "react";
import { jsx as jsx10 } from "react/jsx-runtime";
function Collapse({
  children,
  isOpen,
  horizontal = false,
  openDuration = 500,
  closeDuration = 300
}) {
  const containerRef = useRef3(null);
  const initialOpen = useRef3(isOpen);
  const animationRef = useRef3(0);
  const initialRender = useRef3(true);
  useEffect3(() => {
    const animation = animationRef.current;
    const container = containerRef.current;
    if (animation) {
      clearTimeout(animation);
      animationRef.current = 0;
    }
    if (initialRender.current) {
      return;
    }
    const child = container.children[0];
    if (horizontal) {
      child.style.width = `${child.clientWidth}px`;
      container.style.width = `${child.clientWidth}px`;
    } else {
      container.style.height = `${child.clientHeight}px`;
    }
    if (isOpen) {
      animationRef.current = window.setTimeout(() => {
        container.style.removeProperty("height");
      }, openDuration);
    } else {
      setTimeout(() => {
        if (horizontal) {
          container.style.width = "0";
        } else {
          container.style.height = "0";
        }
      });
    }
  }, [horizontal, isOpen, openDuration]);
  useEffect3(() => {
    initialRender.current = false;
  }, []);
  return /* @__PURE__ */ jsx10(
    "div",
    {
      ref: containerRef,
      className: cn5(
        "_transform-gpu _transition-all _ease-in-out motion-reduce:_transition-none",
        isOpen ? "_opacity-100" : ["_opacity-0", "_overflow-hidden"]
      ),
      style: __spreadProps(__spreadValues({}, initialOpen.current || horizontal ? void 0 : { height: 0 }), {
        transitionDuration: (isOpen ? openDuration : closeDuration) + "ms"
      }),
      children: /* @__PURE__ */ jsx10("div", { children })
    }
  );
}

// src/components/flexsearch.tsx
import cn7 from "clsx";
import { useRouter as useRouter4 } from "nextra/hooks";
import { useCallback as useCallback2, useState as useState4 } from "react";

// src/components/highlight-matches.tsx
import escapeStringRegexp from "escape-string-regexp";
import { memo } from "react";
import { Fragment as Fragment4, jsx as jsx11, jsxs as jsxs4 } from "react/jsx-runtime";
var HighlightMatches = memo(function HighlightMatches2({
  value,
  match
}) {
  if (!value) {
    return null;
  }
  const splitText = value.split("");
  const escapedSearch = escapeStringRegexp(match.trim());
  const regexp = new RegExp(escapedSearch.replaceAll(/\s+/g, "|"), "ig");
  let result;
  let index = 0;
  const content = [];
  while (result = regexp.exec(value)) {
    if (result.index === regexp.lastIndex) {
      regexp.lastIndex++;
    } else {
      const before = splitText.splice(0, result.index - index).join("");
      const after = splitText.splice(0, regexp.lastIndex - result.index).join("");
      content.push(
        before,
        /* @__PURE__ */ jsx11("span", { className: "_text-primary-600", children: after }, result.index)
      );
      index = regexp.lastIndex;
    }
  }
  return /* @__PURE__ */ jsxs4(Fragment4, { children: [
    content,
    splitText.join("")
  ] });
});

// src/components/search.tsx
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions
} from "@headlessui/react";
import cn6 from "clsx";
import NextLink3 from "next/link";
import { useRouter as useRouter3 } from "next/router";
import { useMounted } from "nextra/hooks";
import { InformationCircleIcon, SpinnerIcon } from "nextra/icons";
import { Fragment as Fragment5, useCallback, useEffect as useEffect4, useRef as useRef4, useState as useState3 } from "react";
import { Fragment as Fragment6, jsx as jsx12, jsxs as jsxs5 } from "react/jsx-runtime";
var INPUTS = /* @__PURE__ */ new Set(["input", "select", "button", "textarea"]);
function Search({
  className,
  value,
  onChange,
  onActive,
  loading,
  error,
  results
}) {
  const themeConfig = useThemeConfig();
  const { setMenu } = useMenu();
  const router = useRouter3();
  const [focused, setFocused] = useState3(false);
  const mounted = useMounted();
  const inputRef = useRef4(null);
  useEffect4(() => {
    function down(event) {
      const input = inputRef.current;
      const activeElement = document.activeElement;
      const tagName = activeElement == null ? void 0 : activeElement.tagName.toLowerCase();
      if (!input || !tagName || INPUTS.has(tagName) || (activeElement == null ? void 0 : activeElement.isContentEditable))
        return;
      if (event.key === "/" || event.key === "k" && (event.metaKey || /* for non-Mac */
      event.ctrlKey)) {
        event.preventDefault();
        input.focus({ preventScroll: true });
      }
    }
    window.addEventListener("keydown", down);
    return () => {
      window.removeEventListener("keydown", down);
    };
  }, []);
  const icon = mounted && !focused && /* @__PURE__ */ jsx12(
    "kbd",
    {
      className: cn6(
        "_absolute _my-1.5 _select-none ltr:_right-1.5 rtl:_left-1.5",
        "_h-5 _rounded _bg-white _px-1.5 _font-mono _text-[11px] _font-medium _text-gray-500",
        "_border dark:_border-gray-100/20 dark:_bg-black/50",
        "contrast-more:_border-current contrast-more:_text-current contrast-more:dark:_border-current",
        "_items-center _gap-1 _flex",
        "max-sm:_hidden"
      ),
      children: navigator.userAgent.includes("Mac") ? /* @__PURE__ */ jsxs5(Fragment6, { children: [
        /* @__PURE__ */ jsx12("span", { className: "_text-xs", children: "\u2318" }),
        "K"
      ] }) : "CTRL K"
    }
  );
  const handleFocus = useCallback(
    (event) => {
      const isFocus = event.type === "focus";
      if (isFocus) onActive == null ? void 0 : onActive();
      setFocused(isFocus);
    },
    [onActive]
  );
  const handleChange = useCallback(
    (event) => {
      const { value: value2 } = event.currentTarget;
      onChange(value2);
    },
    [onChange]
  );
  const handleSelect = useCallback(
    (searchResult) => __async(this, null, function* () {
      var _a;
      if (!searchResult) return;
      (_a = inputRef.current) == null ? void 0 : _a.blur();
      yield router.push(searchResult.route);
      setMenu(false);
      onChange("");
    }),
    [router, setMenu, onChange]
  );
  return /* @__PURE__ */ jsxs5(Combobox, { onChange: handleSelect, children: [
    /* @__PURE__ */ jsxs5(
      "div",
      {
        className: cn6(
          "_not-prose",
          // for blog
          "_relative _flex _items-center",
          "_text-gray-900 dark:_text-gray-300",
          "contrast-more:_text-gray-800 contrast-more:dark:_text-gray-300",
          className
        ),
        children: [
          /* @__PURE__ */ jsx12(
            ComboboxInput,
            {
              ref: inputRef,
              spellCheck: false,
              className: ({ focus }) => cn6(
                "_rounded-lg _px-3 _py-2 _transition-colors",
                "_w-full md:_w-64",
                "_text-base _leading-tight md:_text-sm",
                focus ? "_bg-transparent nextra-focusable" : "_bg-black/[.05] dark:_bg-gray-50/10",
                "placeholder:_text-gray-500 dark:placeholder:_text-gray-400",
                "contrast-more:_border contrast-more:_border-current",
                "[&::-webkit-search-cancel-button]:_appearance-none"
              ),
              autoComplete: "off",
              type: "search",
              onChange: handleChange,
              onFocus: handleFocus,
              onBlur: handleFocus,
              value,
              placeholder: renderString(themeConfig.search.placeholder)
            }
          ),
          icon
        ]
      }
    ),
    /* @__PURE__ */ jsx12(
      ComboboxOptions,
      {
        transition: true,
        anchor: { to: "top end", gap: 10, padding: 16 },
        className: ({ open }) => cn6(
          "nextra-search-results",
          // for userspace styling
          "nextra-scrollbar max-md:_h-full",
          "_border _border-gray-200 _text-gray-100 dark:_border-neutral-800",
          "_z-20 _rounded-xl _py-2.5 _shadow-xl",
          "contrast-more:_border contrast-more:_border-gray-900 contrast-more:dark:_border-gray-50",
          "_backdrop-blur-lg _bg-[rgb(var(--nextra-bg),.8)]",
          "motion-reduce:_transition-none _transition-opacity",
          open ? "_opacity-100" : "_opacity-0",
          error || loading || !results.length ? "md:_h-[100px]" : (
            // headlessui adds max-height as style, use !important to override
            "md:!_max-h-[min(calc(100vh-5rem),400px)]"
          ),
          "_w-full md:_w-[576px]",
          "empty:_invisible"
        ),
        children: error ? /* @__PURE__ */ jsxs5("span", { className: "_flex _select-none _justify-center _gap-2 _p-8 _text-center _text-sm _text-red-500", children: [
          /* @__PURE__ */ jsx12(InformationCircleIcon, { className: "_size-5" }),
          renderString(themeConfig.search.error)
        ] }) : loading ? /* @__PURE__ */ jsxs5("span", { className: "_flex _select-none _justify-center _gap-2 _p-8 _text-center _text-sm _text-gray-400", children: [
          /* @__PURE__ */ jsx12(SpinnerIcon, { className: "_size-5 _animate-spin" }),
          renderComponent(themeConfig.search.loading)
        ] }) : results.length ? results.map((searchResult) => /* @__PURE__ */ jsxs5(Fragment5, { children: [
          searchResult.prefix,
          /* @__PURE__ */ jsx12(
            ComboboxOption,
            {
              as: NextLink3,
              value: searchResult,
              href: searchResult.route,
              className: ({ focus }) => cn6(
                "_mx-2.5 _break-words _rounded-md",
                "contrast-more:_border",
                focus ? "_text-primary-600 contrast-more:_border-current _bg-primary-500/10" : "_text-gray-800 dark:_text-gray-300 contrast-more:_border-transparent",
                "_block _scroll-m-12 _px-2.5 _py-2"
              ),
              children: searchResult.children
            }
          )
        ] }, searchResult.id)) : value && renderComponent(themeConfig.search.emptyResult)
      }
    )
  ] });
}

// src/components/flexsearch.tsx
import { Fragment as Fragment7, jsx as jsx13, jsxs as jsxs6 } from "react/jsx-runtime";
var indexes = {};
var loadIndexesPromises = /* @__PURE__ */ new Map();
var loadIndexes = (basePath, locale) => {
  const key = basePath + "@" + locale;
  if (loadIndexesPromises.has(key)) {
    return loadIndexesPromises.get(key);
  }
  const promise = loadIndexesImpl(basePath, locale);
  loadIndexesPromises.set(key, promise);
  return promise;
};
var loadIndexesImpl = (basePath, locale) => __async(void 0, null, function* () {
  const [searchData, Document] = yield Promise.all([
    fetch(`${basePath}/_next/static/chunks/nextra-data-${locale}.json`).then(
      (response) => response.json()
    ),
    import("flexsearch").then((mod) => mod.default.Document)
  ]);
  const pageIndex = new Document({
    cache: 100,
    tokenize: "full",
    document: {
      id: "id",
      index: "content",
      store: ["title"]
    },
    context: {
      resolution: 9,
      depth: 2,
      bidirectional: true
    }
  });
  const sectionIndex = new Document({
    cache: 100,
    tokenize: "full",
    document: {
      id: "id",
      index: "content",
      tag: "pageId",
      store: ["title", "content", "url", "display"]
    },
    context: {
      resolution: 9,
      depth: 2,
      bidirectional: true
    }
  });
  let pageId = 0;
  for (const [route, structurizedData] of Object.entries(searchData)) {
    let pageContent = "";
    ++pageId;
    for (const [key, content] of Object.entries(structurizedData.data)) {
      const [headingId, headingValue] = key.split("#");
      const url = route + (headingId ? "#" + headingId : "");
      const title = headingValue || structurizedData.title;
      const paragraphs = content.split("\n");
      sectionIndex.add(__spreadValues({
        id: url,
        url,
        title,
        pageId: `page_${pageId}`,
        content: title
      }, paragraphs[0] && { display: paragraphs[0] }));
      for (let i = 0; i < paragraphs.length; i++) {
        sectionIndex.add({
          id: `${url}_${i}`,
          url,
          title,
          pageId: `page_${pageId}`,
          content: paragraphs[i]
        });
      }
      pageContent += ` ${title} ${content}`;
    }
    pageIndex.add({
      id: pageId,
      title: structurizedData.title,
      content: pageContent
    });
  }
  indexes[locale] = [pageIndex, sectionIndex];
});
function Flexsearch({
  className
}) {
  const { locale = DEFAULT_LOCALE, basePath } = useRouter4();
  const [loading, setLoading] = useState4(false);
  const [error, setError] = useState4(false);
  const [results, setResults] = useState4([]);
  const [search, setSearch] = useState4("");
  const doSearch = (search2) => {
    var _a, _b;
    if (!search2) {
      setResults([]);
      return;
    }
    const [pageIndex, sectionIndex] = indexes[locale];
    const pageResults = ((_a = pageIndex.search(search2, 5, {
      enrich: true,
      suggest: true
    })[0]) == null ? void 0 : _a.result) || [];
    const results2 = [];
    const pageTitleMatches = {};
    for (let i = 0; i < pageResults.length; i++) {
      const result = pageResults[i];
      pageTitleMatches[i] = 0;
      const sectionResults = ((_b = sectionIndex.search(search2, 5, {
        enrich: true,
        suggest: true,
        tag: `page_${result.id}`
      })[0]) == null ? void 0 : _b.result) || [];
      let isFirstItemOfPage = true;
      const occurred = {};
      for (let j = 0; j < sectionResults.length; j++) {
        const { doc } = sectionResults[j];
        const isMatchingTitle = doc.display !== void 0;
        if (isMatchingTitle) {
          pageTitleMatches[i]++;
        }
        const { url, title } = doc;
        const content = doc.display || doc.content;
        if (occurred[url + "@" + content]) continue;
        occurred[url + "@" + content] = true;
        results2.push({
          _page_rk: i,
          _section_rk: j,
          route: url,
          prefix: isFirstItemOfPage && /* @__PURE__ */ jsx13(
            "div",
            {
              className: cn7(
                "_mx-2.5 _mb-2 [&:not(:first-child)]:_mt-6 _select-none _border-b _border-black/10 _px-2.5 _pb-1.5 _text-xs _font-semibold _uppercase _text-gray-500 dark:_border-white/20 dark:_text-gray-300",
                "contrast-more:_border-gray-600 contrast-more:_text-gray-900 contrast-more:dark:_border-gray-50 contrast-more:dark:_text-gray-50"
              ),
              children: result.doc.title
            }
          ),
          children: /* @__PURE__ */ jsxs6(Fragment7, { children: [
            /* @__PURE__ */ jsx13("div", { className: "_text-base _font-semibold _leading-5", children: /* @__PURE__ */ jsx13(HighlightMatches, { match: search2, value: title }) }),
            content && /* @__PURE__ */ jsx13("div", { className: "excerpt _mt-1 _text-sm _leading-[1.35rem] _text-gray-600 dark:_text-gray-400 contrast-more:dark:_text-gray-50", children: /* @__PURE__ */ jsx13(HighlightMatches, { match: search2, value: content }) })
          ] })
        });
        isFirstItemOfPage = false;
      }
    }
    setResults(
      results2.sort((a, b) => {
        if (a._page_rk === b._page_rk) {
          return a._section_rk - b._section_rk;
        }
        if (pageTitleMatches[a._page_rk] !== pageTitleMatches[b._page_rk]) {
          return pageTitleMatches[b._page_rk] - pageTitleMatches[a._page_rk];
        }
        return a._page_rk - b._page_rk;
      }).map((res) => ({
        id: `${res._page_rk}_${res._section_rk}`,
        route: res.route,
        prefix: res.prefix,
        children: res.children
      }))
    );
  };
  const preload = useCallback2(() => __async(this, null, function* () {
    if (indexes[locale]) return;
    setLoading(true);
    try {
      yield loadIndexes(basePath, locale);
    } catch (e) {
      setError(true);
    }
    setLoading(false);
  }), [locale, basePath]);
  const handleChange = (value) => {
    setSearch(value);
    if (loading) return;
    doSearch(value);
  };
  return /* @__PURE__ */ jsx13(
    Search,
    {
      loading,
      error,
      value: search,
      onChange: handleChange,
      onActive: preload,
      className,
      results
    }
  );
}

// src/components/footer.tsx
import cn9 from "clsx";

// src/components/locale-switch.tsx
import { addBasePath } from "next/dist/client/add-base-path";
import { useRouter as useRouter5 } from "nextra/hooks";
import { GlobeIcon } from "nextra/icons";

// src/components/select.tsx
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions
} from "@headlessui/react";
import cn8 from "clsx";
import { CheckIcon } from "nextra/icons";
import { jsx as jsx14, jsxs as jsxs7 } from "react/jsx-runtime";
function Select({
  options,
  selected,
  onChange,
  title,
  className
}) {
  return /* @__PURE__ */ jsxs7(Listbox, { value: selected, onChange, children: [
    /* @__PURE__ */ jsx14(
      ListboxButton,
      {
        title,
        className: ({ hover, open, focus }) => cn8(
          "_h-7 _rounded-md _px-2 _text-xs _font-medium _transition-colors",
          open ? "_bg-gray-200 _text-gray-900 dark:_bg-primary-100/10 dark:_text-gray-50" : hover ? "_bg-gray-100 _text-gray-900 dark:_bg-primary-100/5 dark:_text-gray-50" : "_text-gray-600 dark:_text-gray-400",
          focus && "nextra-focusable",
          className
        ),
        children: selected.name
      }
    ),
    /* @__PURE__ */ jsx14(
      ListboxOptions,
      {
        as: "ul",
        transition: true,
        anchor: { to: "top start", gap: 10 },
        className: ({ open }) => cn8(
          "nextra-focus",
          open ? "_opacity-100" : "_opacity-0",
          "motion-reduce:_transition-none _transition-opacity _min-w-[--button-width] _z-20 _max-h-64 _rounded-md _border _border-black/5 _backdrop-blur-lg _bg-[rgb(var(--nextra-bg),.8)] _py-1 _text-sm _shadow-lg dark:_border-white/20"
        ),
        children: options.map((option) => /* @__PURE__ */ jsxs7(
          ListboxOption,
          {
            value: option,
            as: "li",
            className: ({ focus }) => cn8(
              focus ? "_bg-primary-50 _text-primary-600 dark:_bg-primary-500/10" : "_text-gray-800 dark:_text-gray-100",
              "_cursor-pointer _whitespace-nowrap _py-1.5 _px-3",
              "_transition-colors",
              option.key === selected.key && "_flex _items-center _justify-between _gap-3"
            ),
            children: [
              option.name,
              option.key === selected.key && /* @__PURE__ */ jsx14(CheckIcon, { height: "16" })
            ]
          },
          option.key
        ))
      }
    )
  ] });
}

// src/components/locale-switch.tsx
import { jsx as jsx15, jsxs as jsxs8 } from "react/jsx-runtime";
var ONE_YEAR = 365 * 24 * 60 * 60 * 1e3;
function LocaleSwitch({
  lite,
  className
}) {
  const themeConfig = useThemeConfig();
  const { locale, asPath } = useRouter5();
  const options = themeConfig.i18n;
  if (!options.length) return null;
  const selected = options.find((l) => locale === l.locale);
  return /* @__PURE__ */ jsx15(
    Select,
    {
      title: "Change language",
      className,
      onChange: (option) => {
        const date = new Date(Date.now() + ONE_YEAR);
        document.cookie = `NEXT_LOCALE=${option.key}; expires=${date.toUTCString()}; path=/`;
        const href = addBasePath(asPath.replace(`/${locale}`, `/${option.key}`));
        location.href = href;
      },
      selected: {
        key: (selected == null ? void 0 : selected.locale) || "",
        name: /* @__PURE__ */ jsxs8("span", { className: "_flex _items-center _gap-2", children: [
          /* @__PURE__ */ jsx15(GlobeIcon, {}),
          /* @__PURE__ */ jsx15("span", { className: lite ? "_hidden" : "", children: selected == null ? void 0 : selected.name })
        ] })
      },
      options: options.map((l) => ({
        key: l.locale,
        name: l.name
      }))
    }
  );
}

// src/components/footer.tsx
import { jsx as jsx16, jsxs as jsxs9 } from "react/jsx-runtime";
function Footer({ menu }) {
  const themeConfig = useThemeConfig();
  return /* @__PURE__ */ jsxs9("footer", { className: "_bg-gray-100 _pb-[env(safe-area-inset-bottom)] dark:_bg-neutral-900 print:_bg-transparent", children: [
    /* @__PURE__ */ jsxs9(
      "div",
      {
        className: cn9(
          "_mx-auto _flex _max-w-[90rem] _gap-2 _py-2 _px-4",
          menu && (themeConfig.i18n.length > 0 || themeConfig.darkMode) ? "_flex" : "_hidden"
        ),
        children: [
          /* @__PURE__ */ jsx16(LocaleSwitch, {}),
          themeConfig.darkMode && renderComponent(themeConfig.themeSwitch.component)
        ]
      }
    ),
    /* @__PURE__ */ jsx16("hr", { className: "dark:_border-neutral-800" }),
    /* @__PURE__ */ jsx16(
      "div",
      {
        className: cn9(
          "_mx-auto _flex _max-w-[90rem] _justify-center _py-12 _text-gray-600 dark:_text-gray-400 md:_justify-start",
          "_pl-[max(env(safe-area-inset-left),1.5rem)] _pr-[max(env(safe-area-inset-right),1.5rem)]"
        ),
        children: renderComponent(themeConfig.footer.content)
      }
    )
  ] });
}

// src/components/head.tsx
import { useTheme } from "next-themes";
import NextHead from "next/head";
import { useMounted as useMounted2 } from "nextra/hooks";
import { Fragment as Fragment8, jsx as jsx17, jsxs as jsxs10 } from "react/jsx-runtime";
function Head() {
  const { resolvedTheme } = useTheme();
  const mounted = useMounted2();
  const themeConfig = useThemeConfig();
  const head = typeof themeConfig.head === "function" ? themeConfig.head({}) : themeConfig.head;
  const { hue, saturation, lightness } = themeConfig.color;
  const { dark: darkHue, light: lightHue } = typeof hue === "number" ? { dark: hue, light: hue } : hue;
  const { dark: darkSaturation, light: lightSaturation } = typeof saturation === "number" ? { dark: saturation, light: saturation } : saturation;
  const { dark: darkLightness, light: lightLightness } = typeof lightness === "number" ? { dark: lightness, light: lightness } : lightness;
  const bgColor = themeConfig.backgroundColor;
  return /* @__PURE__ */ jsxs10(NextHead, { children: [
    themeConfig.faviconGlyph ? /* @__PURE__ */ jsx17(
      "link",
      {
        rel: "icon",
        href: `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><text x='50' y='.9em' font-size='90' text-anchor='middle'>${themeConfig.faviconGlyph}</text><style>text{font-family:system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji";fill:black}@media(prefers-color-scheme:dark){text{fill:white}}</style></svg>`
      }
    ) : null,
    mounted ? /* @__PURE__ */ jsx17(
      "meta",
      {
        name: "theme-color",
        content: resolvedTheme === "dark" ? "#111" : "#fff"
      }
    ) : /* @__PURE__ */ jsxs10(Fragment8, { children: [
      /* @__PURE__ */ jsx17(
        "meta",
        {
          name: "theme-color",
          content: "#fff",
          media: "(prefers-color-scheme: light)"
        }
      ),
      /* @__PURE__ */ jsx17(
        "meta",
        {
          name: "theme-color",
          content: "#111",
          media: "(prefers-color-scheme: dark)"
        }
      )
    ] }),
    /* @__PURE__ */ jsx17(
      "meta",
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1.0, viewport-fit=cover"
      }
    ),
    /* @__PURE__ */ jsx17("style", { children: `:root{--nextra-primary-hue:${lightHue}deg;--nextra-primary-saturation:${lightSaturation}%;--nextra-primary-lightness:${lightLightness}%;--nextra-navbar-height:64px;--nextra-menu-height:3.75rem;--nextra-banner-height:2.5rem;--nextra-bg:${bgColor.light};}.dark{--nextra-primary-hue:${darkHue}deg;--nextra-primary-saturation:${darkSaturation}%;--nextra-primary-lightness:${darkLightness}%;--nextra-bg:${bgColor.dark};}` }),
    head
  ] });
}

// src/components/nav-links.tsx
import cn10 from "clsx";
import NextLink4 from "next/link";
import { ArrowRightIcon as ArrowRightIcon2 } from "nextra/icons";
import { jsx as jsx18, jsxs as jsxs11 } from "react/jsx-runtime";
var classes = {
  link: cn10(
    "nextra-focus _text-gray-600 dark:_text-gray-400",
    "hover:_text-gray-800 dark:hover:_text-gray-200",
    "contrast-more:_text-gray-700 contrast-more:dark:_text-gray-100",
    "_flex _max-w-[50%] _items-center _gap-1 _py-4 _text-base _font-medium _transition-colors [word-break:break-word] md:_text-lg"
  ),
  icon: cn10("_inline _h-5 _shrink-0")
};
function NavLinks({
  flatDocsDirectories,
  currentIndex
}) {
  const themeConfig = useThemeConfig();
  const nav = themeConfig.navigation;
  const navigation = typeof nav === "boolean" ? { prev: nav, next: nav } : nav;
  let prev = navigation.prev && flatDocsDirectories[currentIndex - 1];
  let next = navigation.next && flatDocsDirectories[currentIndex + 1];
  if (prev && !prev.isUnderCurrentDocsTree) prev = false;
  if (next && !next.isUnderCurrentDocsTree) next = false;
  if (!prev && !next) return null;
  return /* @__PURE__ */ jsxs11(
    "div",
    {
      className: cn10(
        "_mb-8 _flex _items-center _border-t _pt-8 dark:_border-neutral-800",
        "contrast-more:_border-neutral-400 dark:contrast-more:_border-neutral-400",
        "print:_hidden"
      ),
      children: [
        prev && /* @__PURE__ */ jsxs11(
          NextLink4,
          {
            href: prev.route,
            title: prev.title,
            className: cn10(classes.link, "ltr:_pr-4 rtl:_pl-4"),
            children: [
              /* @__PURE__ */ jsx18(ArrowRightIcon2, { className: cn10(classes.icon, "ltr:_rotate-180") }),
              prev.title
            ]
          }
        ),
        next && /* @__PURE__ */ jsxs11(
          NextLink4,
          {
            href: next.route,
            title: next.title,
            className: cn10(
              classes.link,
              "ltr:_ml-auto ltr:_pl-4 ltr:_text-right rtl:_mr-auto rtl:_pr-4 rtl:_text-left"
            ),
            children: [
              next.title,
              /* @__PURE__ */ jsx18(ArrowRightIcon2, { className: cn10(classes.icon, "rtl:_rotate-180") })
            ]
          }
        )
      ]
    }
  );
}

// src/components/navbar.tsx
import {
  MenuItem as _MenuItem,
  Menu,
  MenuButton,
  MenuItems
} from "@headlessui/react";
import cn11 from "clsx";
import NextLink5 from "next/link";
import { Button as Button2 } from "nextra/components";
import { useFSRoute as useFSRoute2 } from "nextra/hooks";
import { ArrowRightIcon as ArrowRightIcon3, MenuIcon } from "nextra/icons";
import { jsx as jsx19, jsxs as jsxs12 } from "react/jsx-runtime";
var classes2 = {
  link: cn11(
    "_text-sm contrast-more:_text-gray-700 contrast-more:dark:_text-gray-100"
  ),
  active: cn11("_font-medium _subpixel-antialiased"),
  inactive: cn11(
    "_text-gray-600 hover:_text-gray-800 dark:_text-gray-400 dark:hover:_text-gray-200"
  )
};
function NavbarMenu({
  menu,
  children
}) {
  const routes = Object.fromEntries(
    (menu.children || []).map((route) => [route.name, route])
  );
  return /* @__PURE__ */ jsxs12(Menu, { children: [
    /* @__PURE__ */ jsxs12(
      MenuButton,
      {
        className: ({ focus }) => cn11(
          classes2.link,
          classes2.inactive,
          "max-md:_hidden _items-center _whitespace-nowrap _flex _gap-1.5 _ring-inset",
          focus && "nextra-focusable"
        ),
        children: [
          children,
          /* @__PURE__ */ jsx19(ArrowRightIcon3, { className: "_h-3.5 *:_origin-center *:_transition-transform *:_rotate-90" })
        ]
      }
    ),
    /* @__PURE__ */ jsx19(
      MenuItems,
      {
        transition: true,
        className: ({ open }) => cn11(
          "motion-reduce:_transition-none",
          "nextra-focus",
          open ? "_opacity-100" : "_opacity-0",
          "nextra-scrollbar _transition-opacity",
          "_border _border-black/5 dark:_border-white/20",
          "_backdrop-blur-lg _bg-[rgb(var(--nextra-bg),.8)]",
          "_z-20 _rounded-md _py-1 _text-sm _shadow-lg",
          // headlessui adds max-height as style, use !important to override
          "!_max-h-[min(calc(100vh-5rem),256px)]"
        ),
        anchor: { to: "top end", gap: 10, padding: 16 },
        children: Object.entries(menu.items || {}).map(([key, item]) => {
          var _a;
          return /* @__PURE__ */ jsx19(
            _MenuItem,
            {
              as: Anchor,
              href: item.href || ((_a = routes[key]) == null ? void 0 : _a.route),
              className: ({ focus }) => cn11(
                "_block",
                "_py-1.5 _transition-colors ltr:_pl-3 ltr:_pr-9 rtl:_pr-3 rtl:_pl-9",
                focus ? "_text-gray-900 dark:_text-gray-100" : "_text-gray-600 dark:_text-gray-400"
              ),
              newWindow: item.newWindow,
              children: item.title
            },
            key
          );
        })
      }
    )
  ] });
}
function Navbar({ items }) {
  const themeConfig = useThemeConfig();
  const activeRoute = useFSRoute2();
  const { menu, setMenu } = useMenu();
  return /* @__PURE__ */ jsxs12("div", { className: "nextra-nav-container _sticky _top-0 _z-20 _w-full _bg-transparent print:_hidden", children: [
    /* @__PURE__ */ jsx19("div", { className: "nextra-nav-container-blur" }),
    /* @__PURE__ */ jsxs12("nav", { className: "_mx-auto _flex _h-[var(--nextra-navbar-height)] _max-w-[90rem] _items-center _justify-end _gap-4 _pl-[max(env(safe-area-inset-left),1.5rem)] _pr-[max(env(safe-area-inset-right),1.5rem)]", children: [
      themeConfig.logoLink ? /* @__PURE__ */ jsx19(
        NextLink5,
        {
          href: typeof themeConfig.logoLink === "string" ? themeConfig.logoLink : "/",
          className: "nextra-focus _flex _items-center hover:_opacity-75 ltr:_mr-auto rtl:_ml-auto",
          children: renderComponent(themeConfig.logo)
        }
      ) : /* @__PURE__ */ jsx19("div", { className: "_flex _items-center ltr:_mr-auto rtl:_ml-auto", children: renderComponent(themeConfig.logo) }),
      /* @__PURE__ */ jsx19("div", { className: "_flex _gap-4 _overflow-x-auto nextra-scrollbar _py-1.5", children: items.map((pageOrMenu) => {
        if (pageOrMenu.display === "hidden") return null;
        if (pageOrMenu.type === "menu") {
          const menu2 = pageOrMenu;
          return /* @__PURE__ */ jsx19(NavbarMenu, { menu: menu2, children: menu2.title }, menu2.title);
        }
        const page = pageOrMenu;
        let href = page.href || page.route || "#";
        if (page.children) {
          href = (page.withIndexPage ? page.route : page.firstChildRoute) || href;
        }
        const isActive = page.route === activeRoute || activeRoute.startsWith(page.route + "/");
        return /* @__PURE__ */ jsx19(
          Anchor,
          {
            href,
            className: cn11(
              classes2.link,
              "max-md:_hidden _whitespace-nowrap _ring-inset",
              !isActive || page.newWindow ? classes2.inactive : classes2.active
            ),
            newWindow: page.newWindow,
            "aria-current": !page.newWindow && isActive,
            children: page.title
          },
          href
        );
      }) }),
      process.env.NEXTRA_SEARCH && renderComponent(themeConfig.search.component, {
        className: "max-md:_hidden"
      }),
      themeConfig.project.link ? /* @__PURE__ */ jsx19(Anchor, { href: themeConfig.project.link, newWindow: true, children: renderComponent(themeConfig.project.icon) }) : null,
      themeConfig.chat.link ? /* @__PURE__ */ jsx19(Anchor, { href: themeConfig.chat.link, newWindow: true, children: renderComponent(themeConfig.chat.icon) }) : null,
      renderComponent(themeConfig.navbar.extraContent),
      /* @__PURE__ */ jsx19(
        Button2,
        {
          "aria-label": "Menu",
          className: ({ active }) => cn11(
            "nextra-hamburger _rounded md:_hidden",
            active && "_bg-gray-400/20"
          ),
          onClick: () => setMenu(!menu),
          children: /* @__PURE__ */ jsx19(MenuIcon, { className: cn11({ open: menu }) })
        }
      )
    ] })
  ] });
}

// src/components/404.tsx
import { useRouter as useRouter6 } from "next/router";
import { useMounted as useMounted3 } from "nextra/hooks";
import { jsx as jsx20 } from "react/jsx-runtime";
function NotFoundPage() {
  const themeConfig = useThemeConfig();
  const mounted = useMounted3();
  const { asPath } = useRouter6();
  const { content, labels } = themeConfig.notFound;
  if (!content) {
    return null;
  }
  return /* @__PURE__ */ jsx20("p", { className: "_text-center", children: /* @__PURE__ */ jsx20(
    Anchor,
    {
      href: getGitIssueUrl({
        repository: themeConfig.docsRepositoryBase,
        title: `Found broken \`${mounted ? asPath : ""}\` link. Please fix!`,
        labels
      }),
      newWindow: true,
      className: "_text-primary-600 _underline _decoration-from-font [text-underline-position:from-font]",
      children: renderComponent(content)
    }
  ) });
}

// src/components/sidebar.tsx
import cn12 from "clsx";
import { Button as Button3 } from "nextra/components";
import { useFSRoute as useFSRoute3, useMounted as useMounted4 } from "nextra/hooks";
import { ArrowRightIcon as ArrowRightIcon4, ExpandIcon } from "nextra/icons";
import {
  createContext as createContext5,
  memo as memo2,
  useCallback as useCallback3,
  useContext as useContext5,
  useEffect as useEffect5,
  useMemo as useMemo2,
  useRef as useRef5,
  useState as useState5
} from "react";
import scrollIntoView from "scroll-into-view-if-needed";
import { Fragment as Fragment9, jsx as jsx21, jsxs as jsxs13 } from "react/jsx-runtime";
var TreeState = /* @__PURE__ */ Object.create(null);
var FocusedItemContext = createContext5("");
FocusedItemContext.displayName = "FocusedItem";
var OnFocusItemContext = createContext5(() => {
});
OnFocusItemContext.displayName = "OnFocusItem";
var FolderLevelContext = createContext5(0);
FolderLevelContext.displayName = "FolderLevel";
var Folder = memo2(function FolderInner(props) {
  const level = useContext5(FolderLevelContext);
  return /* @__PURE__ */ jsx21(FolderLevelContext.Provider, { value: level + 1, children: /* @__PURE__ */ jsx21(FolderImpl, __spreadValues({}, props)) });
});
var classes3 = {
  link: cn12(
    "_flex _rounded _px-2 _py-1.5 _text-sm _transition-colors [word-break:break-word]",
    "_cursor-pointer contrast-more:_border"
  ),
  inactive: cn12(
    "_text-gray-500 hover:_bg-gray-100 hover:_text-gray-900",
    "dark:_text-neutral-400 dark:hover:_bg-primary-100/5 dark:hover:_text-gray-50",
    "contrast-more:_text-gray-900 contrast-more:dark:_text-gray-50",
    "contrast-more:_border-transparent contrast-more:hover:_border-gray-900 contrast-more:dark:hover:_border-gray-50"
  ),
  active: cn12(
    "_bg-primary-100 _font-semibold _text-primary-800 dark:_bg-primary-400/10 dark:_text-primary-600",
    "contrast-more:_border-primary-500 contrast-more:dark:_border-primary-500"
  ),
  list: cn12("_flex _flex-col _gap-1"),
  border: cn12(
    "_relative before:_absolute before:_inset-y-1",
    'before:_w-px before:_bg-gray-200 before:_content-[""] dark:before:_bg-neutral-800',
    "ltr:_pl-3 ltr:before:_left-0 rtl:_pr-3 rtl:before:_right-0"
  )
};
function FolderImpl({ item, anchors, onFocus }) {
  const routeOriginal = useFSRoute3();
  const [route] = routeOriginal.split("#");
  const hasRoute = !!item.route;
  const active = hasRoute && [route, route + "/"].includes(item.route + "/");
  const activeRouteInside = active || hasRoute && route.startsWith(item.route + "/");
  const focusedRoute = useContext5(FocusedItemContext);
  const focusedRouteInside = focusedRoute.startsWith(item.route + "/");
  const level = useContext5(FolderLevelContext);
  const { setMenu } = useMenu();
  const { theme } = item;
  const themeConfig = useThemeConfig();
  const open = TreeState[item.route] === void 0 ? active || activeRouteInside || focusedRouteInside || (theme && "collapsed" in theme ? !theme.collapsed : level < themeConfig.sidebar.defaultMenuCollapseLevel) : TreeState[item.route] || focusedRouteInside;
  const rerender = useState5({})[1];
  useEffect5(() => {
    function updateTreeState() {
      if (activeRouteInside || focusedRouteInside) {
        TreeState[item.route] = true;
      }
    }
    function updateAndPruneTreeState() {
      if (activeRouteInside && focusedRouteInside) {
        TreeState[item.route] = true;
      } else {
        delete TreeState[item.route];
      }
    }
    if (themeConfig.sidebar.autoCollapse) {
      updateAndPruneTreeState();
    } else {
      updateTreeState();
    }
  }, [
    activeRouteInside,
    focusedRouteInside,
    item.route,
    themeConfig.sidebar.autoCollapse
  ]);
  if (item.type === "menu") {
    const menu = item;
    const routes = Object.fromEntries(
      (menu.children || []).map((route2) => [route2.name, route2])
    );
    item.children = Object.entries(menu.items || {}).map(([key, item2]) => {
      const route2 = routes[key] || {
        name: key,
        route: menu.route + "/" + key
      };
      return __spreadValues(__spreadValues({}, route2), item2);
    });
  }
  const isLink = "withIndexPage" in item && item.withIndexPage;
  const ComponentToUse = isLink ? Anchor : Button3;
  return /* @__PURE__ */ jsxs13("li", { className: cn12({ open, active }), children: [
    /* @__PURE__ */ jsxs13(
      ComponentToUse,
      {
        href: isLink ? item.route : void 0,
        "data-href": isLink ? void 0 : item.route,
        className: cn12(
          "_items-center _justify-between _gap-2",
          !isLink && "_text-left _w-full",
          classes3.link,
          active ? classes3.active : classes3.inactive
        ),
        onClick: (e) => {
          const clickedToggleIcon = ["svg", "path"].includes(
            e.target.tagName.toLowerCase()
          );
          if (clickedToggleIcon) {
            e.preventDefault();
          }
          if (isLink) {
            if (active || clickedToggleIcon) {
              TreeState[item.route] = !open;
            } else {
              TreeState[item.route] = true;
              setMenu(false);
            }
            rerender({});
            return;
          }
          if (active) return;
          TreeState[item.route] = !open;
          rerender({});
        },
        onFocus,
        children: [
          item.title,
          /* @__PURE__ */ jsx21(
            ArrowRightIcon4,
            {
              height: "18",
              className: cn12(
                "_shrink-0",
                "_rounded-sm _p-0.5 hover:_bg-gray-800/5 dark:hover:_bg-gray-100/5",
                "motion-reduce:*:_transition-none *:_origin-center *:_transition-transform *:rtl:_-rotate-180",
                open && "*:ltr:_rotate-90 *:rtl:_rotate-[-270deg]"
              )
            }
          )
        ]
      }
    ),
    Array.isArray(item.children) && /* @__PURE__ */ jsx21(Collapse, { isOpen: open, children: /* @__PURE__ */ jsx21(
      Menu2,
      {
        className: cn12(classes3.border, "_pt-1 ltr:_ml-3 rtl:_mr-3"),
        directories: item.children,
        anchors
      }
    ) })
  ] });
}
function Separator({ title }) {
  return /* @__PURE__ */ jsx21(
    "li",
    {
      className: cn12(
        "[word-break:break-word]",
        title ? "[&:not(:first-child)]:_mt-5 _mb-2 _px-2 _py-1.5 _text-sm _font-semibold _text-gray-900 dark:_text-gray-100" : "_my-4"
      ),
      children: title ? renderComponent(title) : /* @__PURE__ */ jsx21("hr", { className: "_mx-2 _border-t _border-gray-200 dark:_border-primary-100/10" })
    }
  );
}
function File({
  item,
  anchors,
  onFocus
}) {
  const route = useFSRoute3();
  const active = item.route && [route, route + "/"].includes(item.route + "/");
  const activeAnchor = useActiveAnchor();
  const { setMenu } = useMenu();
  if (item.type === "separator") {
    return /* @__PURE__ */ jsx21(Separator, { title: item.title });
  }
  return /* @__PURE__ */ jsxs13("li", { className: cn12(classes3.list, { active }), children: [
    /* @__PURE__ */ jsx21(
      Anchor,
      {
        href: item.href || item.route,
        newWindow: item.newWindow,
        className: cn12(classes3.link, active ? classes3.active : classes3.inactive),
        onClick: () => {
          setMenu(false);
        },
        onFocus,
        children: item.title
      }
    ),
    active && anchors.length > 0 && /* @__PURE__ */ jsx21("ul", { className: cn12(classes3.list, classes3.border, "ltr:_ml-3 rtl:_mr-3"), children: anchors.map(({ id, value }) => {
      var _a;
      return /* @__PURE__ */ jsx21("li", { children: /* @__PURE__ */ jsx21(
        "a",
        {
          href: `#${id}`,
          className: cn12(
            classes3.link,
            '_flex _gap-2 before:_opacity-25 before:_content-["#"]',
            ((_a = activeAnchor[id]) == null ? void 0 : _a.isActive) ? classes3.active : classes3.inactive
          ),
          onClick: () => {
            setMenu(false);
          },
          children: value
        }
      ) }, id);
    }) })
  ] });
}
function Menu2({
  directories,
  anchors,
  className,
  onlyCurrentDocs
}) {
  const onFocus = useContext5(OnFocusItemContext);
  const handleFocus = useCallback3(
    (event) => {
      const route = event.target.getAttribute("href") || event.target.getAttribute("data-href") || "";
      onFocus(route);
    },
    [onFocus]
  );
  return /* @__PURE__ */ jsx21("ul", { className: cn12(classes3.list, className), children: directories.map((item) => {
    if (onlyCurrentDocs && !item.isUnderCurrentDocsTree) return;
    const ComponentToUse = item.type === "menu" || item.children && (item.children.length || !item.withIndexPage) ? Folder : File;
    return /* @__PURE__ */ jsx21(
      ComponentToUse,
      {
        item,
        anchors,
        onFocus: handleFocus
      },
      item.name
    );
  }) });
}
function Sidebar({
  docsDirectories,
  fullDirectories,
  asPopover = false,
  toc,
  includePlaceholder
}) {
  const { menu, setMenu } = useMenu();
  const [focused, setFocused] = useState5("");
  const [showSidebar, setSidebar] = useState5(true);
  const [showToggleAnimation, setToggleAnimation] = useState5(false);
  const anchors = useMemo2(() => toc.filter((v) => v.depth === 2), [toc]);
  const sidebarRef = useRef5(null);
  const containerRef = useRef5(null);
  const mounted = useMounted4();
  useEffect5(() => {
    const activeElement = sidebarRef.current.querySelector("li.active");
    if (activeElement && (window.innerWidth > 767 || menu)) {
      const scroll = () => {
        scrollIntoView(activeElement, {
          block: "center",
          inline: "center",
          scrollMode: "always",
          boundary: containerRef.current
        });
      };
      if (menu) {
        setTimeout(scroll, 300);
      } else {
        scroll();
      }
    }
  }, [menu]);
  const themeConfig = useThemeConfig();
  const hasI18n = themeConfig.i18n.length > 0;
  const hasMenu = themeConfig.darkMode || hasI18n || themeConfig.sidebar.toggleButton;
  return /* @__PURE__ */ jsxs13(Fragment9, { children: [
    includePlaceholder && asPopover && /* @__PURE__ */ jsx21("div", { className: "max-xl:_hidden _h-0 _w-64 _shrink-0" }),
    /* @__PURE__ */ jsx21(
      "div",
      {
        className: cn12(
          "[transition:background-color_1.5s_ease]",
          menu ? "_bg-black/80 dark:_bg-black/60 _fixed _inset-0 _z-10 md:_hidden" : "_bg-transparent"
        ),
        onClick: () => setMenu(false)
      }
    ),
    /* @__PURE__ */ jsxs13(
      "aside",
      {
        className: cn12(
          "nextra-sidebar-container _flex _flex-col",
          "md:_top-16 md:_shrink-0 motion-reduce:_transform-none motion-reduce:_transition-none",
          "[.resizing_&]:_transition-none",
          "_transform-gpu _transition-all _ease-in-out",
          "print:_hidden",
          showSidebar ? "md:_w-64" : "md:_w-20",
          asPopover ? "md:_hidden" : "md:_sticky md:_self-start",
          menu ? "max-md:[transform:translate3d(0,0,0)]" : "max-md:[transform:translate3d(0,-100%,0)]"
        ),
        ref: containerRef,
        children: [
          process.env.NEXTRA_SEARCH && /* @__PURE__ */ jsx21("div", { className: "_px-4 _pt-4 md:_hidden", children: renderComponent(themeConfig.search.component) }),
          /* @__PURE__ */ jsx21(FocusedItemContext.Provider, { value: focused, children: /* @__PURE__ */ jsx21(OnFocusItemContext.Provider, { value: setFocused, children: /* @__PURE__ */ jsxs13(
            "div",
            {
              className: cn12(
                "_overflow-y-auto",
                "_p-4 _grow md:_h-[calc(100vh-var(--nextra-navbar-height)-var(--nextra-menu-height))]",
                showSidebar ? "nextra-scrollbar" : "no-scrollbar"
              ),
              ref: sidebarRef,
              children: [
                (!asPopover || !showSidebar) && /* @__PURE__ */ jsx21(Collapse, { isOpen: showSidebar, horizontal: true, children: /* @__PURE__ */ jsx21(
                  Menu2,
                  {
                    className: "nextra-menu-desktop max-md:_hidden",
                    directories: docsDirectories,
                    anchors: themeConfig.toc.float ? [] : anchors,
                    onlyCurrentDocs: true
                  }
                ) }),
                mounted && window.innerWidth < 768 && /* @__PURE__ */ jsx21(
                  Menu2,
                  {
                    className: "nextra-menu-mobile md:_hidden",
                    directories: fullDirectories,
                    anchors
                  }
                )
              ]
            }
          ) }) }),
          hasMenu && /* @__PURE__ */ jsxs13(
            "div",
            {
              className: cn12(
                "nextra-sidebar-footer _sticky _bottom-0",
                "_flex _items-center _gap-2 _py-4",
                "_mx-3 _px-1",
                // to hide focused sidebar links
                showSidebar ? hasI18n && "_justify-end" : "_py-4 _flex-wrap _justify-center"
              ),
              "data-toggle-animation": showToggleAnimation ? showSidebar ? "show" : "hide" : "off",
              children: [
                /* @__PURE__ */ jsx21(
                  LocaleSwitch,
                  {
                    lite: !showSidebar,
                    className: showSidebar ? "_grow" : "max-md:_grow"
                  }
                ),
                themeConfig.darkMode && /* @__PURE__ */ jsx21(
                  "div",
                  {
                    className: showSidebar && !hasI18n ? "_grow _flex _flex-col" : "",
                    children: renderComponent(themeConfig.themeSwitch.component, {
                      lite: !showSidebar || hasI18n
                    })
                  }
                ),
                themeConfig.sidebar.toggleButton && /* @__PURE__ */ jsx21(
                  Button3,
                  {
                    title: showSidebar ? "Hide sidebar" : "Show sidebar",
                    className: ({ hover }) => cn12(
                      "max-md:_hidden _rounded-md _p-2",
                      hover ? "_bg-gray-100 _text-gray-900 dark:_bg-primary-100/5 dark:_text-gray-50" : "_text-gray-600 dark:_text-gray-400"
                    ),
                    onClick: () => {
                      setSidebar(!showSidebar);
                      setToggleAnimation(true);
                    },
                    children: /* @__PURE__ */ jsx21(
                      ExpandIcon,
                      {
                        height: "12",
                        className: cn12(
                          !showSidebar && "first:*:_origin-[35%] first:*:_rotate-180"
                        )
                      }
                    )
                  }
                )
              ]
            }
          )
        ]
      }
    )
  ] });
}

// src/components/skip-nav.tsx
import cn13 from "clsx";
import { forwardRef as forwardRef2 } from "react";
import { jsx as jsx22 } from "react/jsx-runtime";
var DEFAULT_ID = "reach-skip-nav";
var DEFAULT_LABEL = "Skip to content";
var SkipNavLink = forwardRef2(
  (_a, forwardedRef) => {
    var _b = _a, {
      className: providedClassName,
      id,
      label = DEFAULT_LABEL,
      styled
    } = _b, props = __objRest(_b, [
      "className",
      "id",
      "label",
      "styled"
    ]);
    const className = providedClassName === void 0 ? styled ? cn13(
      "_sr-only",
      "nextra-focus",
      "focus:_not-sr-only focus:_fixed focus:_z-50 focus:_m-3 focus:_ml-4 focus:_h-[calc(var(--nextra-navbar-height)-1.5rem)] focus:_rounded-lg focus:_border focus:_px-3 focus:_py-2 focus:_align-middle focus:_text-sm focus:_font-bold",
      "focus:_text-gray-900 focus:dark:_text-gray-100",
      "focus:_bg-white focus:dark:_bg-neutral-900",
      "focus:_border-neutral-400 focus:dark:_border-neutral-800"
    ) : "" : providedClassName;
    return /* @__PURE__ */ jsx22(
      "a",
      __spreadProps(__spreadValues({}, props), {
        ref: forwardedRef,
        href: `#${id || DEFAULT_ID}`,
        className,
        children: label
      })
    );
  }
);
SkipNavLink.displayName = "SkipNavLink";
var SkipNavContent = forwardRef2(
  (_a, forwardedRef) => {
    var _b = _a, { id } = _b, props = __objRest(_b, ["id"]);
    return /* @__PURE__ */ jsx22("div", __spreadProps(__spreadValues({}, props), { ref: forwardedRef, id: id || DEFAULT_ID }));
  }
);
SkipNavContent.displayName = "SkipNavContent";

// src/components/theme-switch.tsx
import { useTheme as useTheme2 } from "next-themes";
import { useMounted as useMounted5 } from "nextra/hooks";
import { MoonIcon, SunIcon } from "nextra/icons";
import { jsx as jsx23, jsxs as jsxs14 } from "react/jsx-runtime";
function ThemeSwitch({
  lite,
  className
}) {
  const { setTheme, resolvedTheme, theme = "" } = useTheme2();
  const mounted = useMounted5();
  const config = useThemeConfig().themeSwitch;
  const IconToUse = mounted && resolvedTheme === "dark" ? MoonIcon : SunIcon;
  const options = typeof config.useOptions === "function" ? config.useOptions() : config.useOptions;
  return /* @__PURE__ */ jsx23(
    Select,
    {
      className,
      title: "Change theme",
      options: [
        { key: "light", name: options.light },
        { key: "dark", name: options.dark },
        { key: "system", name: options.system }
      ],
      onChange: (option) => {
        setTheme(option.key);
      },
      selected: {
        key: theme,
        name: /* @__PURE__ */ jsxs14("div", { className: "_flex _items-center _gap-2 _capitalize", children: [
          /* @__PURE__ */ jsx23(IconToUse, {}),
          /* @__PURE__ */ jsx23("span", { className: lite ? "md:_hidden" : "", children: mounted ? options[theme] : options.light })
        ] })
      }
    }
  );
}

// src/components/toc.tsx
import cn15 from "clsx";
import { removeLinks } from "nextra/remove-links";
import { useEffect as useEffect6, useRef as useRef6 } from "react";
import scrollIntoView2 from "scroll-into-view-if-needed";

// src/components/back-to-top.tsx
import cn14 from "clsx";
import { Button as Button4 } from "nextra/components";
import { ArrowRightIcon as ArrowRightIcon5 } from "nextra/icons";
import { jsx as jsx24, jsxs as jsxs15 } from "react/jsx-runtime";
var SCROLL_TO_OPTIONS = { top: 0, behavior: "smooth" };
var scrollToTop = (event) => {
  const buttonElement = event.currentTarget;
  const tocElement = buttonElement.parentElement.parentElement;
  window.scrollTo(SCROLL_TO_OPTIONS);
  tocElement.scrollTo(SCROLL_TO_OPTIONS);
  buttonElement.disabled = true;
};
function BackToTop({
  children,
  className,
  hidden
}) {
  return /* @__PURE__ */ jsxs15(
    Button4,
    {
      "aria-hidden": hidden ? "true" : void 0,
      onClick: scrollToTop,
      disabled: hidden,
      className: ({ disabled }) => cn14(
        "_flex _items-center _gap-1.5",
        "_whitespace-nowrap",
        // Safari
        disabled ? "_opacity-0" : "_opacity-100",
        className
      ),
      children: [
        children,
        /* @__PURE__ */ jsx24(
          ArrowRightIcon5,
          {
            height: "16",
            className: "_-rotate-90 _border _rounded-full _border-current"
          }
        )
      ]
    }
  );
}

// src/components/toc.tsx
import { Fragment as Fragment10, jsx as jsx25, jsxs as jsxs16 } from "react/jsx-runtime";
var linkClassName = cn15(
  "_text-xs _font-medium",
  "_text-gray-600 dark:_text-gray-400",
  "hover:_text-gray-800 dark:hover:_text-gray-200",
  "contrast-more:_text-gray-700 contrast-more:dark:_text-gray-100"
);
function TOC({ toc, filePath }) {
  var _a;
  const activeAnchor = useActiveAnchor();
  const tocRef = useRef6(null);
  const themeConfig = useThemeConfig();
  const hasHeadings = toc.length > 0;
  const hasMetaInfo = Boolean(
    themeConfig.feedback.content || themeConfig.editLink.component || themeConfig.toc.extraContent || themeConfig.toc.backToTop
  );
  const activeSlug = (_a = Object.entries(activeAnchor).find(
    ([, { isActive }]) => isActive
  )) == null ? void 0 : _a[0];
  const activeIndex = toc.findIndex(({ id }) => id === activeSlug);
  useEffect6(() => {
    var _a2;
    if (!activeSlug) return;
    const anchor = (_a2 = tocRef.current) == null ? void 0 : _a2.querySelector(`a[href="#${activeSlug}"]`);
    if (anchor) {
      scrollIntoView2(anchor, {
        behavior: "smooth",
        block: "center",
        inline: "center",
        scrollMode: "if-needed",
        boundary: tocRef.current
      });
    }
  }, [activeSlug]);
  return /* @__PURE__ */ jsxs16(
    "div",
    {
      className: cn15(
        hasHeadings && "_grid _grid-rows-[min-content_1fr_min-content]",
        // 1fr: toc headings, min-content: title/footer
        "_sticky _top-[--nextra-navbar-height] _pt-6 _text-sm",
        "_max-h-[calc(100vh-var(--nextra-navbar-height))]"
      ),
      children: [
        hasHeadings && /* @__PURE__ */ jsxs16(Fragment10, { children: [
          /* @__PURE__ */ jsx25(
            "p",
            {
              className: cn15(
                "_mx-4",
                // use margin instead padding to not have shadow on scrollbar
                "_font-semibold _tracking-tight",
                "_pb-2 _shadow-[0_12px_16px_rgb(var(--nextra-bg))] contrast-more:_shadow-none _z-[1]"
              ),
              children: renderComponent(themeConfig.toc.title)
            }
          ),
          /* @__PURE__ */ jsx25(
            "ul",
            {
              ref: tocRef,
              className: cn15(
                "_px-4 nextra-scrollbar _overscroll-y-contain _overflow-y-auto _hyphens-auto",
                "_py-1.5"
                // for title/footer shadow
              ),
              children: toc.map(({ id, value, depth }) => {
                var _a2;
                return /* @__PURE__ */ jsx25("li", { className: "_my-2 _scroll-my-6 _scroll-py-6", children: /* @__PURE__ */ jsx25(
                  "a",
                  {
                    href: `#${id}`,
                    className: cn15(
                      "nextra-focus",
                      {
                        2: "_font-semibold",
                        3: "_ms-4",
                        4: "_ms-8",
                        5: "_ms-12",
                        6: "_ms-16"
                      }[depth],
                      "_block _transition-colors _subpixel-antialiased",
                      ((_a2 = activeAnchor[id]) == null ? void 0 : _a2.isActive) ? "_text-primary-600 contrast-more:!_text-primary-600" : "_text-gray-500 hover:_text-gray-900 dark:_text-gray-400 dark:hover:_text-gray-300",
                      "contrast-more:_text-gray-900 contrast-more:_underline contrast-more:dark:_text-gray-50 _break-words"
                    ),
                    children: removeLinks(value)
                  }
                ) }, id);
              })
            }
          )
        ] }),
        hasMetaInfo && /* @__PURE__ */ jsxs16(
          "div",
          {
            className: cn15(
              hasHeadings && "nextra-toc-footer _pt-4",
              "_flex _flex-col _items-start _gap-2 _pb-4",
              "_mx-4"
              // for border top width
            ),
            children: [
              themeConfig.feedback.content ? /* @__PURE__ */ jsx25(
                Anchor,
                {
                  className: linkClassName,
                  href: themeConfig.feedback.useLink(),
                  newWindow: true,
                  children: renderComponent(themeConfig.feedback.content)
                }
              ) : null,
              renderComponent(themeConfig.editLink.component, {
                filePath,
                className: linkClassName,
                children: renderComponent(themeConfig.editLink.content)
              }),
              renderComponent(themeConfig.toc.extraContent),
              themeConfig.toc.backToTop && /* @__PURE__ */ jsx25(BackToTop, { className: linkClassName, hidden: activeIndex < 2, children: renderComponent(themeConfig.toc.backToTop) })
            ]
          }
        )
      ]
    }
  );
}

// src/mdx-components.tsx
import cn16 from "clsx";
import { Code, Pre, Table, Td, Th, Tr } from "nextra/components";
import { useMounted as useMounted6 } from "nextra/hooks";
import { ArrowRightIcon as ArrowRightIcon6 } from "nextra/icons";
import {
  Children,
  cloneElement,
  useEffect as useEffect7,
  useMemo as useMemo3,
  useRef as useRef7,
  useState as useState6
} from "react";
import { Fragment as Fragment11, jsx as jsx26, jsxs as jsxs17 } from "react/jsx-runtime";
var createHeading = (Tag, context) => function Heading(_a) {
  var _b = _a, {
    children,
    id,
    className
  } = _b, props = __objRest(_b, [
    "children",
    "id",
    "className"
  ]);
  const setActiveAnchor = useSetActiveAnchor();
  const slugs2 = useSlugs();
  const observer = useIntersectionObserver();
  const obRef = useRef7(null);
  useEffect7(() => {
    const heading = obRef.current;
    if (!id || !observer || !heading) return;
    observer.observe(heading);
    slugs2.set(heading, [id, context.index += 1]);
    return () => {
      observer.disconnect();
      slugs2.delete(heading);
      setActiveAnchor((f) => {
        const ret = __spreadValues({}, f);
        delete ret[id];
        return ret;
      });
    };
  }, [id, slugs2, observer, setActiveAnchor]);
  return /* @__PURE__ */ jsxs17(
    Tag,
    __spreadProps(__spreadValues({
      id,
      className: (
        // can be added by footnotes
        className === "sr-only" ? "_sr-only" : cn16(
          "_font-semibold _tracking-tight _text-slate-900 dark:_text-slate-100",
          {
            h2: "_mt-10 _border-b _pb-1 _text-3xl _border-neutral-200/70 contrast-more:_border-neutral-400 dark:_border-primary-100/10 contrast-more:dark:_border-neutral-400",
            h3: "_mt-8 _text-2xl",
            h4: "_mt-8 _text-xl",
            h5: "_mt-8 _text-lg",
            h6: "_mt-8 _text-base"
          }[Tag],
          className
        )
      )
    }, props), {
      children: [
        children,
        id && /* @__PURE__ */ jsx26(
          "a",
          {
            href: `#${id}`,
            className: "nextra-focus subheading-anchor",
            "aria-label": "Permalink for this section",
            ref: obRef
          }
        )
      ]
    })
  );
};
function Details(_a) {
  var _b = _a, {
    children,
    open,
    className
  } = _b, props = __objRest(_b, [
    "children",
    "open",
    "className"
  ]);
  const [isOpen, setIsOpen] = useState6(() => !!open);
  const [delayedOpenState, setDelayedOpenState] = useState6(isOpen);
  const animationRef = useRef7(0);
  useEffect7(() => {
    const animation = animationRef.current;
    if (animation) {
      clearTimeout(animation);
      animationRef.current = 0;
    }
    if (!isOpen) {
      animationRef.current = window.setTimeout(
        () => setDelayedOpenState(isOpen),
        300
      );
      return () => {
        clearTimeout(animationRef.current);
      };
    }
    setDelayedOpenState(true);
  }, [isOpen]);
  const [summaryElement, restChildren] = useMemo3(
    function findSummary(list = children) {
      let summary;
      const rest = Children.map(list, (child) => {
        if (!summary && // Add onClick only for first summary
        child && typeof child === "object" && "type" in child) {
          if (child.type === Summary) {
            summary = cloneElement(child, {
              onClick(event) {
                event.preventDefault();
                setIsOpen((v) => !v);
              }
            });
            return;
          }
          if (child.type !== Details && child.props.children) {
            ;
            [summary, child] = findSummary(child.props.children);
          }
        }
        return child;
      });
      return [summary, rest];
    },
    [children]
  );
  return /* @__PURE__ */ jsxs17(
    "details",
    __spreadProps(__spreadValues({
      className: cn16(
        "[&:not(:first-child)]:_mt-4 _rounded _border _border-gray-200 _bg-white _p-2 _shadow-sm dark:_border-neutral-800 dark:_bg-neutral-900",
        "_overflow-hidden",
        className
      )
    }, props), {
      open: isOpen || delayedOpenState,
      "data-expanded": isOpen ? "" : void 0,
      children: [
        summaryElement,
        /* @__PURE__ */ jsx26(Collapse, { isOpen, children: restChildren })
      ]
    })
  );
}
function Summary(_a) {
  var _b = _a, {
    children,
    className
  } = _b, props = __objRest(_b, [
    "children",
    "className"
  ]);
  return /* @__PURE__ */ jsxs17(
    "summary",
    __spreadProps(__spreadValues({
      className: cn16(
        "nextra-focus",
        "_flex _items-center _cursor-pointer _p-1 _transition-colors hover:_bg-gray-100 dark:hover:_bg-neutral-800",
        "[&::-webkit-details-marker]:_hidden",
        // Safari
        // display: flex removes whitespace when `<summary>` contains text with other elements, like `foo <strong>bar</strong>`
        "_whitespace-pre-wrap",
        "_select-none",
        className
      )
    }, props), {
      children: [
        children,
        /* @__PURE__ */ jsx26(
          ArrowRightIcon6,
          {
            className: cn16(
              "_order-first",
              // if prettier formats `summary` it will have unexpected margin-top
              "_h-4 _shrink-0 _mx-1.5 motion-reduce:_transition-none",
              "rtl:_rotate-180 [[data-expanded]>summary:first-child>&]:_rotate-90 _transition"
            ),
            strokeWidth: "3"
          }
        )
      ]
    })
  );
}
var EXTERNAL_HREF_REGEX = /https?:\/\//;
var Link = (_a) => {
  var _b = _a, { href = "", className } = _b, props = __objRest(_b, ["href", "className"]);
  return /* @__PURE__ */ jsx26(
    Anchor,
    __spreadValues({
      href,
      newWindow: EXTERNAL_HREF_REGEX.test(href),
      className: cn16(
        "_text-primary-600 _underline hover:_no-underline _decoration-from-font [text-underline-position:from-font]",
        className
      )
    }, props)
  );
};
var A = (_a) => {
  var _b = _a, { href = "" } = _b, props = __objRest(_b, ["href"]);
  return /* @__PURE__ */ jsx26(Anchor, __spreadValues({ href, newWindow: EXTERNAL_HREF_REGEX.test(href) }, props));
};
var classes4 = {
  toc: cn16(
    "nextra-toc _order-last max-xl:_hidden _w-64 _shrink-0 print:_hidden"
  ),
  main: cn16("_w-full _break-words")
};
function Body({ children }) {
  const config = useConfig();
  const themeConfig = useThemeConfig();
  const mounted = useMounted6();
  const {
    activeThemeContext: themeContext,
    activeType,
    activeIndex,
    flatDocsDirectories,
    activePath
  } = config.normalizePagesResult;
  if (themeContext.layout === "raw") {
    return /* @__PURE__ */ jsx26("div", { className: classes4.main, children });
  }
  const date = themeContext.timestamp && themeConfig.gitTimestamp && config.timestamp ? new Date(config.timestamp) : null;
  const gitTimestampEl = (
    // Because a user's time zone may be different from the server page
    mounted && date ? /* @__PURE__ */ jsx26("div", { className: "_mt-12 _mb-8 _block _text-xs _text-gray-500 ltr:_text-right rtl:_text-left dark:_text-gray-400", children: renderComponent(themeConfig.gitTimestamp, { timestamp: date }) }) : /* @__PURE__ */ jsx26("div", { className: "_mt-16" })
  );
  const content = /* @__PURE__ */ jsxs17(Fragment11, { children: [
    renderComponent(themeContext.topContent),
    children,
    gitTimestampEl,
    renderComponent(themeContext.bottomContent),
    activeType !== "page" && themeContext.pagination && /* @__PURE__ */ jsx26(
      NavLinks,
      {
        flatDocsDirectories,
        currentIndex: activeIndex
      }
    )
  ] });
  const body = themeConfig.main ? /* @__PURE__ */ jsx26(themeConfig.main, { children: content }) : content;
  if (themeContext.layout === "full") {
    return /* @__PURE__ */ jsx26(
      "article",
      {
        className: cn16(
          classes4.main,
          "nextra-content _min-h-[calc(100vh-var(--nextra-navbar-height))] _pl-[max(env(safe-area-inset-left),1.5rem)] _pr-[max(env(safe-area-inset-right),1.5rem)]"
        ),
        children: body
      }
    );
  }
  return /* @__PURE__ */ jsx26(
    "article",
    {
      className: cn16(
        classes4.main,
        "nextra-content _flex _min-h-[calc(100vh-var(--nextra-navbar-height))] _min-w-0 _justify-center _pb-8 _pr-[calc(env(safe-area-inset-right)-1.5rem)]",
        themeContext.typesetting === "article" && "nextra-body-typesetting-article"
      ),
      children: /* @__PURE__ */ jsxs17("main", { className: "_w-full _min-w-0 _max-w-6xl _px-6 _pt-4 md:_px-12", children: [
        activeType !== "page" && themeContext.breadcrumb && /* @__PURE__ */ jsx26(Breadcrumb, { activePath }),
        body
      ] })
    }
  );
}
var DEFAULT_COMPONENTS = {
  h1: (props) => /* @__PURE__ */ jsx26(
    "h1",
    __spreadValues({
      className: "_mt-2 _text-4xl _font-bold _tracking-tight _text-slate-900 dark:_text-slate-100"
    }, props)
  ),
  ul: (props) => /* @__PURE__ */ jsx26(
    "ul",
    __spreadValues({
      className: "[:is(ol,ul)_&]:_my-3 [&:not(:first-child)]:_mt-6 _list-disc ltr:_ml-6 rtl:_mr-6"
    }, props)
  ),
  ol: (props) => /* @__PURE__ */ jsx26(
    "ol",
    __spreadValues({
      className: "[:is(ol,ul)_&]:_my-3 [&:not(:first-child)]:_mt-6 _list-decimal ltr:_ml-6 rtl:_mr-6"
    }, props)
  ),
  li: (props) => /* @__PURE__ */ jsx26("li", __spreadValues({ className: "_my-2" }, props)),
  blockquote: (props) => /* @__PURE__ */ jsx26(
    "blockquote",
    __spreadValues({
      className: cn16(
        "[&:not(:first-child)]:_mt-6 _border-gray-300 _italic _text-gray-700 dark:_border-gray-700 dark:_text-gray-400",
        "ltr:_border-l-2 ltr:_pl-6 rtl:_border-r-2 rtl:_pr-6"
      )
    }, props)
  ),
  hr: (props) => /* @__PURE__ */ jsx26(
    "hr",
    __spreadValues({
      className: "_my-8 _border-neutral-200/70 contrast-more:_border-neutral-400 dark:_border-primary-100/10 contrast-more:dark:_border-neutral-400"
    }, props)
  ),
  a: Link,
  table: (props) => /* @__PURE__ */ jsx26(
    Table,
    __spreadProps(__spreadValues({}, props), {
      className: cn16(
        "nextra-scrollbar [&:not(:first-child)]:_mt-6 _p-0",
        props.className
      )
    })
  ),
  p: (props) => /* @__PURE__ */ jsx26("p", __spreadValues({ className: "[&:not(:first-child)]:_mt-6 _leading-7" }, props)),
  tr: Tr,
  th: Th,
  td: Td,
  details: Details,
  summary: Summary,
  pre: Pre,
  code: Code,
  wrapper: function NextraWrapper({ toc, children }) {
    const config = useConfig();
    const themeConfig = useThemeConfig();
    const {
      activeType,
      activeThemeContext: themeContext,
      docsDirectories,
      directories
    } = config.normalizePagesResult;
    const tocEl = activeType === "page" || !themeContext.toc || themeContext.layout !== "default" ? themeContext.layout !== "full" && themeContext.layout !== "raw" && /* @__PURE__ */ jsx26("nav", { className: classes4.toc, "aria-label": "table of contents" }) : /* @__PURE__ */ jsx26("nav", { className: classes4.toc, "aria-label": "table of contents", children: renderComponent(themeConfig.toc.component, {
      toc: themeConfig.toc.float ? toc : [],
      filePath: config.filePath
    }) });
    return /* @__PURE__ */ jsxs17(
      "div",
      {
        className: cn16(
          "_mx-auto _flex",
          themeContext.layout !== "raw" && "_max-w-[90rem]"
        ),
        children: [
          /* @__PURE__ */ jsx26(
            Sidebar,
            {
              docsDirectories,
              fullDirectories: directories,
              toc,
              asPopover: config.hideSidebar,
              includePlaceholder: themeContext.layout === "default"
            }
          ),
          tocEl,
          /* @__PURE__ */ jsx26(SkipNavContent, {}),
          /* @__PURE__ */ jsx26(Body, { children })
        ]
      }
    );
  }
};
function getComponents({
  isRawLayout,
  components
}) {
  if (isRawLayout) {
    return { a: A, wrapper: DEFAULT_COMPONENTS.wrapper };
  }
  const context = { index: 0 };
  return __spreadValues(__spreadProps(__spreadValues({}, DEFAULT_COMPONENTS), {
    h2: createHeading("h2", context),
    h3: createHeading("h3", context),
    h4: createHeading("h4", context),
    h5: createHeading("h5", context),
    h6: createHeading("h6", context)
  }), components);
}

// src/index.tsx
import { useTheme as useTheme3 } from "next-themes";
import { jsx as jsx27, jsxs as jsxs18 } from "react/jsx-runtime";
function InnerLayout({ children }) {
  const themeConfig = useThemeConfig();
  const config = useConfig();
  const { locale } = useRouter7();
  const { direction } = themeConfig.i18n.find((l) => l.locale === locale) || themeConfig;
  const dir = direction === "rtl" ? "rtl" : "ltr";
  const { activeThemeContext: themeContext, topLevelNavbarItems } = config.normalizePagesResult;
  const components = getComponents({
    isRawLayout: themeContext.layout === "raw",
    components: themeConfig.components
  });
  return /* @__PURE__ */ jsx27(
    ThemeProvider,
    __spreadProps(__spreadValues({
      attribute: "class",
      disableTransitionOnChange: true
    }, themeConfig.nextThemes), {
      children: /* @__PURE__ */ jsxs18("div", { dir, children: [
        /* @__PURE__ */ jsx27(
          "script",
          {
            dangerouslySetInnerHTML: {
              __html: `document.documentElement.setAttribute('dir','${dir}')`
            }
          }
        ),
        /* @__PURE__ */ jsx27(Head, {}),
        /* @__PURE__ */ jsx27(Banner, {}),
        themeContext.navbar && renderComponent(themeConfig.navbar.component, {
          items: topLevelNavbarItems
        }),
        /* @__PURE__ */ jsx27(ActiveAnchorProvider, { children: /* @__PURE__ */ jsx27(MDXProvider, { disableParentContext: true, components, children }) }),
        themeContext.footer && renderComponent(themeConfig.footer.component, {
          menu: config.hideSidebar
        })
      ] })
    })
  );
}
function Layout({
  children,
  themeConfig,
  pageOpts
}) {
  return /* @__PURE__ */ jsx27(ThemeConfigProvider, { value: themeConfig, children: /* @__PURE__ */ jsx27(ConfigProvider, { value: pageOpts, children: /* @__PURE__ */ jsx27(InnerLayout, { children }) }) });
}
export {
  Bleed,
  Collapse,
  Link,
  LocaleSwitch,
  Navbar,
  NotFoundPage,
  SkipNavContent,
  SkipNavLink,
  ThemeSwitch,
  Layout as default,
  getComponents,
  useConfig,
  useMenu,
  useTheme3 as useTheme,
  useThemeConfig
};
